import { useQuery } from "convex/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { isConvexConfigured } from "@/lib/convex";
import { gateRedirectPlatform, gatedArtistSlug, isGateLocked } from "@/utils/artistGate";
import { resolveArtistGatePhase, type UnlockPhase } from "@/utils/mailingUnlock";
import { MAILING_EMAIL_CHANGED_EVENT, getSubscribedEmail, getUnlockToken } from "../mailing/mailingStorage";

export interface ArtistGateLock {
  slug: string | null;
  locked: boolean;
  resolving: boolean;
  phase: UnlockPhase;
  storedEmail: string | null;
  setStoredEmail: (email: string | null) => void;
  bypassReason: "viewer" | null;
  redirectPlatform: string | null;
}

export function useArtistGateLock(): ArtistGateLock {
  const location = useLocation();
  const slug = useMemo(() => gatedArtistSlug(location.pathname), [location.pathname]);
  const redirectPlatform = useMemo(() => gateRedirectPlatform(location.pathname, location.search), [location.pathname, location.search]);
  const [storedEmail, setStoredEmail] = useState<string | null>(() => getSubscribedEmail());
  const [unlockToken, setUnlockToken] = useState<string | null>(() => getUnlockToken());

  useEffect(() => {
    setStoredEmail(getSubscribedEmail());
    setUnlockToken(getUnlockToken());
  }, [slug]);

  useEffect(() => {
    const sync = () => {
      setStoredEmail(getSubscribedEmail());
      setUnlockToken(getUnlockToken());
    };
    window.addEventListener(MAILING_EMAIL_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(MAILING_EMAIL_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const viewer = useQuery(api.viewer.me, isConvexConfigured && slug ? {} : "skip");
  const check = useQuery(
    api.mailingList.checkUnlock,
    isConvexConfigured && slug && storedEmail && unlockToken ? { email: storedEmail, token: unlockToken } : "skip",
  );

  const phase = resolveArtistGatePhase({ storedEmail: unlockToken ? storedEmail : null, check });
  const viewerBypass = Boolean(viewer?.isAuthenticated);
  const waitingOnViewer = Boolean(slug) && isConvexConfigured && viewer === undefined;
  const waitingOnCheck = Boolean(slug) && isConvexConfigured && phase === "checking";
  const resolving = waitingOnViewer || waitingOnCheck;

  const updateStoredEmail = useCallback((email: string | null) => setStoredEmail(email), []);

  if (!slug || !isConvexConfigured) {
    return { slug: null, locked: false, resolving: false, phase: "closed", storedEmail, setStoredEmail: updateStoredEmail, bypassReason: null, redirectPlatform: null };
  }

  return {
    slug,
    locked: isGateLocked({
      slug,
      backendReady: isConvexConfigured,
      resolving,
      viewerAuthenticated: viewerBypass,
      unlocked: phase === "unlocked",
    }),
    resolving,
    phase,
    storedEmail,
    setStoredEmail: updateStoredEmail,
    bypassReason: viewerBypass ? "viewer" : null,
    redirectPlatform,
  };
}
